import React, { useState, useEffect, useMemo } from "react";
import {
    Container,
    Typography,
    Box,
    Paper,
    Grid,
    Card,
    CardContent,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    Alert,
    Divider,
    Chip,
} from "@mui/material";
import {
    AttachMoney,
    ShoppingCart,
    LocalShipping,
    Person,
    Inventory,
    PieChart,
    TrendingUp,
    ErrorOutline,
    Payment,
    AccountBalance,
    CreditCard,
    CardGiftcard,
    CallSplit,
} from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { OrderStatisticsResponse } from "../../types";
import { formatCurrencyBDT } from "../../utils/currency";
import { PaginatedStatsList } from "../../components/orders/PaginatedStatsList";
import { orderService } from "../../services/orderService";
import { parseOrderSearchParamsFromUrl } from "../../utils/orderSearchParams";
import {
    orderChannelService,
    OrderChannelSimple,
} from "../../services/orderChannelService";
import {
    deliveryService,
    DeliveryServiceSimple,
} from "../../services/deliveryService";

interface StatCardProps {
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
    subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({
    title,
    value,
    icon,
    color,
    subtitle,
}) => (
    <Card
        elevation={2}
        sx={{
            height: "100%",
            bgcolor: `${color}10`,
            border: 2,
            borderColor: `${color}30`,
        }}
    >
        <CardContent>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 2,
                }}
            >
                <Box
                    sx={{
                        bgcolor: `${color}20`,
                        p: 1.5,
                        borderRadius: 2,
                        color: color,
                        display: "flex",
                    }}
                >
                    {icon}
                </Box>
            </Box>
            <Typography
                variant="h3"
                component="div"
                sx={{ fontWeight: "bold", mb: 1 }}
            >
                {value}
            </Typography>
            <Typography
                variant="body1"
                color="text.secondary"
                sx={{ fontWeight: 600 }}
            >
                {title}
            </Typography>
            {subtitle && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                >
                    {subtitle}
                </Typography>
            )}
        </CardContent>
    </Card>
);

export const OrderStatistics: React.FC = () => {
    const [searchParams] = useSearchParams();
    const [stats, setStats] = useState<OrderStatisticsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [orderChannels, setOrderChannels] = useState<OrderChannelSimple[]>(
        [],
    );
    const [deliveryServices, setDeliveryServices] = useState<
        DeliveryServiceSimple[]
    >([]);

    const orderSearchParams = useMemo(
        () => parseOrderSearchParamsFromUrl(searchParams),
        [searchParams],
    );

    // Loaded once for resolving specific channel/service IDs to human-readable
    // names in the "Filters Applied" chip below.
    useEffect(() => {
        orderChannelService
            .getOrderChannelsSimple(true)
            .then(setOrderChannels)
            .catch(() => setOrderChannels([]));
        deliveryService
            .getDeliveryServicesSimple(true)
            .then(setDeliveryServices)
            .catch(() => setDeliveryServices([]));
    }, []);

    // Generate a description of the current filters
    const getFilterDescription = () => {
        const parts = [];
        if (orderSearchParams.status)
            parts.push(`Status: ${orderSearchParams.status}`);
        if (orderSearchParams.payment_status)
            parts.push(`Payment: ${orderSearchParams.payment_status}`);
        if (
            orderSearchParams.customer_ids &&
            orderSearchParams.customer_ids.length > 0
        )
            parts.push(
                `Customers: ${orderSearchParams.customer_ids.length} selected`,
            );
        if (
            orderSearchParams.product_ids &&
            orderSearchParams.product_ids.length > 0
        )
            parts.push(
                `Products: ${orderSearchParams.product_ids.length} selected`,
            );
        if (
            orderSearchParams.order_date_from ||
            orderSearchParams.order_date_to
        )
            parts.push(
                `Date: ${orderSearchParams.order_date_from || "Start"} to ${
                    orderSearchParams.order_date_to || "Now"
                }`,
            );

        if (orderSearchParams.order_channel_id) {
            const channel = orderChannels.find(
                (c) => c.order_channel_id === orderSearchParams.order_channel_id,
            );
            parts.push(`Channel: ${channel?.name || "Selected"}`);
        } else if (orderSearchParams.order_channel_type) {
            parts.push(`Channel Type: ${orderSearchParams.order_channel_type}`);
        }

        if (orderSearchParams.delivery_service_id) {
            const service = deliveryServices.find(
                (s) =>
                    s.delivery_service_id ===
                    orderSearchParams.delivery_service_id,
            );
            parts.push(`Delivery: ${service?.name || "Selected"}`);
        } else if (orderSearchParams.delivery_service_type) {
            parts.push(
                `Delivery Type: ${orderSearchParams.delivery_service_type}`,
            );
        }

        if (orderSearchParams.has_gift_items !== undefined) {
            parts.push(
                orderSearchParams.has_gift_items
                    ? "Contains Gift Items"
                    : "No Gift Items",
            );
        }

        return parts.length > 0 ? parts.join(" • ") : "All Orders";
    };

    useEffect(() => {
        const fetchStats = async () => {
            setLoading(true);
            setError(null);
            try {
                const data =
                    await orderService.getOrderStatistics(orderSearchParams);
                setStats(data);
            } catch (err: any) {
                console.error("Failed to load order statistics:", err);
                setError(
                    err.response?.data?.detail ||
                        err.message ||
                        "Failed to load statistics",
                );
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, [orderSearchParams]);

    if (loading) {
        return (
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "60vh",
                    }}
                >
                    <CircularProgress size={60} />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 3,
                        minHeight: "60vh",
                        justifyContent: "center",
                    }}
                >
                    <ErrorOutline
                        color="error"
                        sx={{ fontSize: 80, opacity: 0.5 }}
                    />
                    <Alert severity="error" sx={{ maxWidth: 600 }}>
                        {error}
                    </Alert>
                </Box>
            </Container>
        );
    }

    if (!stats) {
        return (
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Box sx={{ textAlign: "center", py: 8 }}>
                    <Typography variant="h6" color="text.secondary">
                        No statistics available
                    </Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 1,
                    }}
                >
                    <PieChart sx={{ fontSize: 40 }} color="primary" />
                    <Typography variant="h3" component="h1" fontWeight="bold">
                        Order Statistics & Analytics
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="subtitle1" color="text.secondary">
                        Filters Applied:
                    </Typography>
                    <Chip
                        label={getFilterDescription()}
                        color="primary"
                        variant="outlined"
                    />
                </Box>
            </Box>

            {/* Overview Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Total Orders"
                        value={stats.total_orders}
                        icon={<ShoppingCart sx={{ fontSize: 32 }} />}
                        color="#2196f3"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Total Revenue"
                        value={formatCurrencyBDT(stats.total_amount)}
                        icon={<AttachMoney sx={{ fontSize: 32 }} />}
                        color="#4caf50"
                        subtitle={`Avg: ${formatCurrencyBDT(
                            stats.average_order_value,
                        )}`}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Total Paid"
                        value={formatCurrencyBDT(stats.total_paid)}
                        icon={<Payment sx={{ fontSize: 32 }} />}
                        color="#9c27b0"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Total Due"
                        value={formatCurrencyBDT(stats.total_due)}
                        icon={<AccountBalance sx={{ fontSize: 32 }} />}
                        color="#f44336"
                    />
                </Grid>
            </Grid>

            {/* Secondary Financial Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Delivery Revenue"
                        value={formatCurrencyBDT(stats.delivery_stats.revenue)}
                        subtitle="Charged to customers"
                        icon={<LocalShipping sx={{ fontSize: 28 }} />}
                        color="#4caf50"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Delivery Cost"
                        value={formatCurrencyBDT(stats.delivery_stats.cost)}
                        subtitle="Paid to partners"
                        icon={<LocalShipping sx={{ fontSize: 28 }} />}
                        color="#f44336"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Delivery Profit"
                        value={formatCurrencyBDT(stats.delivery_stats.profit)}
                        subtitle={`${stats.delivery_stats.profit_margin}% margin`}
                        icon={<TrendingUp sx={{ fontSize: 28 }} />}
                        color="#2196f3"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Total Discount"
                        value={formatCurrencyBDT(stats.total_discount)}
                        icon={<TrendingUp sx={{ fontSize: 28 }} />}
                        color="#00bcd4"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Cost of Gifts Given"
                        value={formatCurrencyBDT(stats.total_gift_cost || 0)}
                        subtitle={
                            stats.gift_items_count
                                ? `${stats.gift_items_count} gift item(s)`
                                : undefined
                        }
                        icon={<CardGiftcard sx={{ fontSize: 28 }} />}
                        color="#e91e63"
                    />
                </Grid>
            </Grid>

            {/* Breakdowns Section */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Order Status Breakdown */}
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Paper elevation={2} sx={{ p: 3, height: "100%" }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 2,
                            }}
                        >
                            <TrendingUp color="action" />
                            <Typography variant="h6" fontWeight="bold">
                                Order Status
                            </Typography>
                        </Box>
                        <List dense>
                            {stats.status_breakdown.map((item, index) => (
                                <React.Fragment key={item.label}>
                                    <ListItem sx={{ px: 0 }}>
                                        <ListItemText
                                            primary={
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="medium"
                                                >
                                                    {item.label}
                                                </Typography>
                                            }
                                            secondary={`${item.count} orders (${item.percentage}%)`}
                                        />
                                        <Typography
                                            variant="body2"
                                            fontWeight="bold"
                                            color="primary"
                                        >
                                            {formatCurrencyBDT(item.value)}
                                        </Typography>
                                    </ListItem>
                                    {index <
                                        stats.status_breakdown.length - 1 && (
                                        <Divider component="li" />
                                    )}
                                </React.Fragment>
                            ))}
                        </List>
                    </Paper>
                </Grid>

                {/* Order Channel Breakdown */}
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Paper elevation={2} sx={{ p: 3, height: "100%" }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 2,
                            }}
                        >
                            <CallSplit color="action" />
                            <Typography variant="h6" fontWeight="bold">
                                Order Channel
                            </Typography>
                        </Box>
                        <List dense>
                            {stats.channel_breakdown.length > 0 ? (
                                stats.channel_breakdown.map((item, index) => (
                                    <React.Fragment key={item.label}>
                                        <ListItem sx={{ px: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="medium"
                                                    >
                                                        {item.label}
                                                    </Typography>
                                                }
                                                secondary={`${item.count} orders (${item.percentage}%)`}
                                            />
                                            <Typography
                                                variant="body2"
                                                fontWeight="bold"
                                                color="primary"
                                            >
                                                {formatCurrencyBDT(item.value)}
                                            </Typography>
                                        </ListItem>
                                        {index <
                                            stats.channel_breakdown.length -
                                                1 && (
                                            <Divider component="li" />
                                        )}
                                    </React.Fragment>
                                ))
                            ) : (
                                <Typography
                                    color="text.secondary"
                                    align="center"
                                    sx={{ py: 4 }}
                                >
                                    No data available
                                </Typography>
                            )}
                        </List>
                    </Paper>
                </Grid>

                {/* Delivery Service Performance */}
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Paper elevation={2} sx={{ p: 3, height: "100%" }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 2,
                            }}
                        >
                            <LocalShipping color="action" />
                            <Typography variant="h6" fontWeight="bold">
                                Delivery Service Performance
                            </Typography>
                        </Box>
                        <List dense>
                            {stats.delivery_service_breakdown.length > 0 ? (
                                stats.delivery_service_breakdown.map(
                                    (item, index) => (
                                        <React.Fragment key={item.name}>
                                            <ListItem
                                                sx={{
                                                    px: 0,
                                                    flexDirection: "column",
                                                    alignItems: "stretch",
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent:
                                                            "space-between",
                                                        width: "100%",
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="medium"
                                                    >
                                                        {item.name}
                                                        {item.type
                                                            ? ` (${item.type})`
                                                            : ""}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="bold"
                                                        color={
                                                            item.profit >= 0
                                                                ? "success.main"
                                                                : "error.main"
                                                        }
                                                    >
                                                        {formatCurrencyBDT(
                                                            item.profit,
                                                        )}
                                                    </Typography>
                                                </Box>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {item.count} orders •
                                                    Revenue{" "}
                                                    {formatCurrencyBDT(
                                                        item.revenue,
                                                    )}{" "}
                                                    • Cost{" "}
                                                    {formatCurrencyBDT(
                                                        item.cost,
                                                    )}{" "}
                                                    • {item.profit_margin}%
                                                    margin
                                                </Typography>
                                            </ListItem>
                                            {index <
                                                stats.delivery_service_breakdown
                                                    .length -
                                                    1 && (
                                                <Divider component="li" />
                                            )}
                                        </React.Fragment>
                                    ),
                                )
                            ) : (
                                <Typography
                                    color="text.secondary"
                                    align="center"
                                    sx={{ py: 4 }}
                                >
                                    No data available
                                </Typography>
                            )}
                        </List>
                    </Paper>
                </Grid>

                {/* Payment Status Breakdown */}
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Paper elevation={2} sx={{ p: 3, height: "100%" }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 2,
                            }}
                        >
                            <Payment color="action" />
                            <Typography variant="h6" fontWeight="bold">
                                Payment Status
                            </Typography>
                        </Box>
                        <List dense>
                            {stats.payment_status_breakdown.map(
                                (item, index) => (
                                    <React.Fragment key={item.label}>
                                        <ListItem sx={{ px: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="medium"
                                                    >
                                                        {item.label}
                                                    </Typography>
                                                }
                                                secondary={`${item.count} orders (${item.percentage}%)`}
                                            />
                                            <Typography
                                                variant="body2"
                                                fontWeight="bold"
                                                color="secondary"
                                            >
                                                {formatCurrencyBDT(item.value)}
                                            </Typography>
                                        </ListItem>
                                        {index <
                                            stats.payment_status_breakdown
                                                .length -
                                                1 && <Divider component="li" />}
                                    </React.Fragment>
                                ),
                            )}
                        </List>
                    </Paper>
                </Grid>

                {/* Payment Methods Breakdown */}
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Paper elevation={2} sx={{ p: 3, height: "100%" }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 2,
                            }}
                        >
                            <CreditCard color="action" />
                            <Typography variant="h6" fontWeight="bold">
                                Payment Methods
                            </Typography>
                        </Box>
                        <List dense>
                            {stats.payment_methods_breakdown.map(
                                (item, index) => (
                                    <React.Fragment key={item.label}>
                                        <ListItem sx={{ px: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="medium"
                                                    >
                                                        {item.label}
                                                    </Typography>
                                                }
                                                secondary={`${item.count} transactions (${item.percentage}%)`}
                                            />
                                            <Typography
                                                variant="body2"
                                                fontWeight="bold"
                                                color="success.main"
                                            >
                                                {formatCurrencyBDT(item.value)}
                                            </Typography>
                                        </ListItem>
                                        {index <
                                            stats.payment_methods_breakdown
                                                .length -
                                                1 && <Divider component="li" />}
                                    </React.Fragment>
                                ),
                            )}
                        </List>
                    </Paper>
                </Grid>

                {/* Top Products by Quantity */}
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <PaginatedStatsList
                        title="Top Products (Qty)"
                        icon={<Inventory color="action" />}
                        fetchData={(params, skip, limit) =>
                            orderService.getTopProductsStats(
                                params,
                                skip,
                                limit,
                            )
                        }
                        searchParams={orderSearchParams}
                        initialData={stats.top_products}
                        unitLabel="units"
                    />
                </Grid>

                {/* Top Products by Amount */}
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <PaginatedStatsList
                        title="Top Products (Amount)"
                        icon={<TrendingUp color="action" />}
                        fetchData={(params, skip, limit) =>
                            orderService.getTopProductsByAmountStats(
                                params,
                                skip,
                                limit,
                            )
                        }
                        searchParams={orderSearchParams}
                        initialData={stats.top_products}
                        unitLabel="units"
                    />
                </Grid>

                {/* Top Customers */}
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <PaginatedStatsList
                        title="Top Customers"
                        icon={<Person color="action" />}
                        fetchData={(params, skip, limit) =>
                            orderService.getTopCustomersStats(
                                params,
                                skip,
                                limit,
                            )
                        }
                        searchParams={orderSearchParams}
                        initialData={stats.top_customers}
                        unitLabel="orders"
                    />
                </Grid>
            </Grid>
        </Container>
    );
};
